import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, Pagination, Stack, Typography } from '@mui/material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ProductsGrid from './ProductsGrid';
import CategoryFilters from './CategoryFilters';
import axios from 'axios';

const PAGE_SIZE = 17;

const ProductsPage = () => {
  const { categorySlug, subcategorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Math.max(Number(searchParams.get('page') || 1), 1);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();
  const [redirectedTo404, setRedirectedTo404] = useState(false);

  const fetchProducts = useCallback(async (pageNum, category, subcategory) => {
    setLoading(true);
    const url = subcategory
      ? `/api/compositions/?page=${pageNum}&categories=${category}&categories=${subcategory}`
      : `/api/compositions/?page=${pageNum}&categories=${category}`;

    try {
      const response = await axios.get(url);
      const calculatedTotalPages = response.data.count
        ? Math.max(1, Math.ceil(response.data.count / PAGE_SIZE))
        : 1;

      setProducts(response.data.results || []);
      setTotalPages(calculatedTotalPages);
    } catch (error) {
      if (!redirectedTo404 && error.response && (error.response.status === 400 || error.response.status === 404)) {
        setRedirectedTo404(true);
        navigate('*');
      } else {
        console.error('Ошибка при загрузке товаров:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, redirectedTo404]);

  useEffect(() => {
    fetchProducts(currentPage, categorySlug, subcategorySlug);
  }, [currentPage, categorySlug, subcategorySlug, fetchProducts]);

  const handleFilterChange = () => {
    setSearchParams({});
  };

  const handlePageChange = (_, newPage) => {
    const nextParams = new URLSearchParams(searchParams);

    if (newPage === 1) {
      nextParams.delete('page');
    } else {
      nextParams.set('page', String(newPage));
    }

    setSearchParams(nextParams);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <CategoryFilters
        onFilterChange={handleFilterChange}
        categorySlug={categorySlug}
        subcategorySlug={subcategorySlug}
      />
      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Stack>
      ) : products.length ? (
        <>
          <ProductsGrid products={products} />
          <Stack alignItems="center" sx={{ my: 4 }}>
            <Pagination
              count={totalPages}
              page={Math.min(currentPage, totalPages)}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
            />
          </Stack>
        </>
      ) : (
        <Typography align="center" sx={{ py: 8 }}>
          Товары не найдены.
        </Typography>
      )}
    </Box>
  );
};

export default ProductsPage;
